// Admin variant of the profile editor. Matches docs/mockups/admin-edit.html.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { AdminUserDetail, AdminUserUpdate } from "../api/admin";
import {
  approveUser,
  getUser,
  rotateAuthKey,
  setPassword,
  softDeleteUser,
  restoreUser,
  updateUser,
} from "../api/admin";
import { ApiError } from "../api/client";
import { Chrome } from "../components/Chrome";

export function AdminEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [draft, setDraft] = useState<AdminUserUpdate>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pulse, setPulse] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwPulse, setPwPulse] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const u = await getUser(Number(id));
      setUser(u);
      setDraft({});
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Couldn't load user");
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const patch = (p: AdminUserUpdate) => setDraft((d) => ({ ...d, ...p }));
  const field = <K extends keyof AdminUserUpdate>(key: K) => ({
    value: ((draft[key] as string | number | null | undefined) ??
      (user?.[key as keyof AdminUserDetail] as string | number | null | undefined) ??
      "") as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      patch({ [key]: e.target.value || null } as AdminUserUpdate),
  });

  async function save() {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateUser(user.id, draft);
      setUser(updated);
      setDraft({});
      setPulse("Saved");
      setTimeout(() => setPulse(null), 2400);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function doRotate() {
    if (!user) return;
    if (!confirm(`Rotate auth_key for ${user.username}? This signs them out of all sessions.`)) return;
    setBusy(true);
    try {
      await rotateAuthKey(user.id);
      setPulse("auth_key rotated");
      setTimeout(() => setPulse(null), 2400);
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    if (!user) return;
    if (!confirm(`Soft-delete ${user.username}? Their public page returns 404 immediately.`)) return;
    await softDeleteUser(user.id);
    await refresh();
  }

  async function doRestore() {
    if (!user) return;
    await restoreUser(user.id);
    await refresh();
  }

  async function doApprove() {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await approveUser(user.id);
      setUser(updated);
      setPulse("Account approved → ACTIVE");
      setTimeout(() => setPulse(null), 2800);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Approve failed");
    } finally {
      setBusy(false);
    }
  }

  async function doSetPassword() {
    if (!user) return;
    if (newPassword.length < 10) {
      setPwError("Password must be at least 10 characters.");
      return;
    }
    if (!confirm(
      `Set a new password for ${user.username}? This invalidates all their existing sessions.`,
    )) return;
    setPwBusy(true);
    setPwError(null);
    try {
      await setPassword(user.id, newPassword);
      setNewPassword("");
      setPwPulse("Password updated · share new credentials out-of-band");
      setTimeout(() => setPwPulse(null), 3600);
    } catch (err) {
      setPwError(err instanceof ApiError ? err.detail : "Set password failed");
    } finally {
      setPwBusy(false);
    }
  }

  if (!user) {
    return (
      <>
        <Chrome tag="Admin · Editing" />
        <main className="grid min-h-[40vh] place-items-center">
          <span className="eyebrow">{error ?? "loading…"}</span>
        </main>
      </>
    );
  }

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username;

  return (
    <>
      <Chrome tag="Admin · Editing" />
      <main className="mx-auto max-w-7xl px-7 pb-32 pt-7">
        {/* Acting-as banner */}
        <div className="relative mb-7 flex items-center gap-4 overflow-hidden rounded-lg bg-ink p-4 text-paper">
          <span className="absolute inset-y-0 left-0 w-1 bg-terra" />
          <div className="size-10 rounded-full border border-paper/20 bg-gradient-to-br from-terra to-terra-deep" />
          <div>
            <div className="font-display tracking-tight">Editing {displayName}</div>
            <div className="font-mono text-xs text-paper/55">
              varo.biz/{user.username} · admin · changes are logged
            </div>
          </div>
          <div className="ml-auto flex gap-2">
            <Link
              to={`/${user.username}`}
              target="_blank"
              className="btn btn-ghost text-xs text-paper"
            >
              View public ↗
            </Link>
            <Link to="/admin" className="btn btn-ghost text-xs text-paper">
              All users
            </Link>
          </div>
        </div>

        <div className="grid gap-7 lg:grid-cols-[1fr_360px]">
          <div>
            <p className="eyebrow eyebrow-accent">01. Identity</p>
            <h1 className="mt-3 mb-6 text-4xl tracking-tight">
              Editing {displayName}
            </h1>

            <div className="space-y-5 rounded-lg border border-rule-soft bg-paper-deep p-6">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="First name" {...field("first_name")} />
                <Field label="Last name" {...field("last_name")} />
                <Field label="Email" value={user.email} disabled />
                <Field label="Username" value={user.username} disabled />
                <Field label="Phone" {...field("phone")} />
                <Field label="Website" {...field("web")} />
                <Field label="Address" {...field("address")} />
                <Field label="Designation" {...field("designation")} />
              </div>
            </div>

            <p className="eyebrow eyebrow-accent mt-9">02. Content</p>
            <h2 className="mt-3 mb-6 text-2xl tracking-tight">Card content</h2>
            <div className="space-y-5 rounded-lg border border-rule-soft bg-paper-deep p-6">
              <Field label="About heading" {...field("about_heading")} />
              <div>
                <label className="field-label">About text</label>
                <textarea
                  rows={4}
                  className="field-input"
                  value={(draft.about_text ?? user.about_text ?? "") as string}
                  onChange={(e) => patch({ about_text: e.target.value || null })}
                />
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Video heading" {...field("video_heading")} />
                <Field label="Video URL" type="url" {...field("video_url")} />
              </div>
            </div>
          </div>

          <aside className="space-y-5 lg:sticky lg:top-7 self-start">
            {user.status === 8 && (
              <div className="rounded-lg border-2 border-terra bg-terra/10 p-6">
                <div className="mb-3 flex items-baseline justify-between">
                  <h4 className="text-lg text-terra-deep">Pending approval</h4>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-terra-deep">
                    status 8
                  </span>
                </div>
                <p className="mb-4 text-sm text-ink-soft">
                  Email is verified. This account cannot sign in until you
                  approve it.
                </p>
                <button
                  onClick={doApprove}
                  disabled={busy}
                  className="btn btn-primary btn-sm w-full"
                >
                  {busy ? "Approving…" : "Approve account → ACTIVE"}
                </button>
              </div>
            )}

            <Panel title="Admin fields" subtitle="Owner can't see">
              <div>
                <label className="field-label">Status</label>
                <select
                  className="field-input"
                  value={(draft.status ?? user.status) as number}
                  onChange={(e) => patch({ status: Number(e.target.value) })}
                >
                  <option value={10}>10 · Active</option>
                  <option value={8}>8 · Pending admin approval</option>
                  <option value={9}>9 · Inactive (email unverified)</option>
                  <option value={0}>0 · Deleted (soft)</option>
                </select>
              </div>
              <div className="mt-5">
                <label className="field-label">Type</label>
                <select
                  className="field-input"
                  value={(draft.type ?? user.type) as number}
                  onChange={(e) => patch({ type: Number(e.target.value) })}
                >
                  <option value={2}>2 · User</option>
                  <option value={1}>1 · Super admin</option>
                </select>
              </div>
              <div className="mt-5">
                <span className="field-label">Rotate auth_key</span>
                <button onClick={doRotate} className="btn btn-secondary btn-sm w-full">
                  Rotate (sign out everywhere)
                </button>
              </div>
            </Panel>

            <Panel title="Reset password" subtitle="Operational">
              <p className="mb-3 text-xs text-ink-soft">
                Set a new password. Existing sessions are invalidated; share
                the new value out-of-band.
              </p>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={10}
                maxLength={128}
                placeholder="New password (min 10 chars)"
                className="field-input"
              />
              <button
                onClick={doSetPassword}
                disabled={pwBusy || newPassword.length < 10}
                className="btn btn-secondary btn-sm mt-3 w-full"
              >
                {pwBusy ? "Setting…" : "Set password"}
              </button>
              {pwError && (
                <div className="mt-3 font-mono text-[11px] text-plum">{pwError}</div>
              )}
              {pwPulse && (
                <div className="mt-3 font-mono text-[11px] text-moss">{pwPulse}</div>
              )}
            </Panel>

            <Panel title="Account">
              <Row k="ID" v={`#${user.id}`} />
              <Row k="Created" v={new Date(user.created_at).toLocaleDateString()} />
              <Row k="Updated" v={new Date(user.updated_at).toLocaleDateString()} />
              <Row k="Email verified" v={user.has_pending_email_verification ? "Pending" : "Yes"} />
              <Row k="Hash prefix" v={<code className="font-mono text-xs">{user.password_hash_prefix}…</code>} />
            </Panel>

            <Panel title="Danger zone" danger>
              {user.status === 0 ? (
                <button onClick={doRestore} className="btn btn-secondary btn-sm w-full text-moss border-moss">
                  Restore (set status → Active)
                </button>
              ) : (
                <button onClick={doDelete} className="btn btn-secondary btn-sm w-full text-plum border-plum">
                  Soft-delete user
                </button>
              )}
            </Panel>
          </aside>
        </div>

        <div className="fixed inset-x-0 bottom-4 z-40 mx-auto flex max-w-3xl items-center gap-4 rounded-full border border-rule bg-cream-card px-5 py-3 shadow-lg">
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-mute">
            {error ? <span className="text-plum">{error}</span> : pulse ? <span className="text-moss">{pulse}</span> : Object.keys(draft).length > 0 ? "Unsaved admin changes" : "Up to date"}
          </span>
          <span className="ml-auto flex gap-2">
            <button onClick={() => navigate("/admin")} className="btn btn-ghost text-xs">
              Back to list
            </button>
            <button onClick={save} disabled={busy || Object.keys(draft).length === 0} className="btn btn-primary text-xs">
              {busy ? "Saving…" : "Save"}
            </button>
          </span>
        </div>
      </main>
    </>
  );
}

interface FieldP {
  label: string;
  value: string;
  type?: string;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
function Field({ label, value, type = "text", disabled, onChange }: FieldP) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={onChange}
        className={`field-input ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      />
    </div>
  );
}

function Panel({
  title,
  subtitle,
  children,
  danger,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-6 ${
        danger
          ? "border-plum/30 bg-gradient-to-br from-cream-card to-[#F4E6E1]"
          : "border-rule bg-cream-card"
      }`}
    >
      <header className="mb-4 flex items-baseline justify-between">
        <h4 className={`text-lg ${danger ? "text-plum" : ""}`}>{title}</h4>
        {subtitle && (
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-terra-deep">
            {subtitle}
          </span>
        )}
      </header>
      {children}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-3 py-1">
      <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-mute">{k}</span>
      <span className="text-sm">{v}</span>
    </div>
  );
}
